// Upload cache utility for managing guest uploads that need to be resumed after login

export interface CachedUpload {
  // File info (for ZIP uploads)
  name?: string;
  type?: string;
  size?: number;
  contents?: string; // base64 encoded file contents
  
  // Upload metadata
  source_type: 'zip' | 'github';
  scan_id: string; // temporary scan ID for demo, will be replaced with real scan_id
  timestamp: number;
  demoResults?: any; // Store demo scan results
  
  // GitHub specific
  repoUrl?: string; // for GitHub uploads
  
  // Redirect intent
  redirectPath?: string; // e.g., '/results/{scan_id}'
}

const CACHE_KEY = 'pending_upload';
const CACHE_EXPIRY_HOURS = 2; // Cache expires after 2 hours
const MAX_LOCALSTORAGE_SIZE = 4 * 1024 * 1024; // 4MB safe limit for localStorage

/**
 * Check available localStorage space and estimated size needed
 */
function checkStorageSpace(dataSize: number): { canStore: boolean; reason?: string } {
  try {
    // Estimate JSON overhead (roughly 1.3x for base64 + metadata)
    const estimatedSize = dataSize * 1.4;
    
    if (estimatedSize > MAX_LOCALSTORAGE_SIZE) {
      return { 
        canStore: false, 
        reason: `File too large for cache (${(estimatedSize / 1024 / 1024).toFixed(1)}MB > 4MB limit)` 
      };
    }
    
    // Test if we can actually store the data by creating a test entry
    try {
      const testKey = 'storage_test';
      const testData = 'x'.repeat(Math.min(100000, estimatedSize)); // Test with sample data
      localStorage.setItem(testKey, testData);
      localStorage.removeItem(testKey);
      return { canStore: true };
    } catch (testError) {
      return { 
        canStore: false, 
        reason: 'Insufficient localStorage space available' 
      };
    }
  } catch (error) {
    return { 
      canStore: false, 
      reason: 'Unable to check storage space' 
    };
  }
}

/**
 * Store an uploaded ZIP file in localStorage for later processing after login
 */
export async function cacheZipUploadForLater(file: File, demoResults?: any): Promise<string> {
  console.log('[uploadCache.cacheZipUploadForLater] Caching ZIP file for post-login processing:', {
    name: file.name,
    size: file.size,
    type: file.type
  });

  try {
    // Check if file is too large for localStorage before processing
    const storageCheck = checkStorageSpace(file.size);
    if (!storageCheck.canStore) {
      console.warn('[uploadCache.cacheZipUploadForLater] ⚠️ File too large for cache:', storageCheck.reason);
      throw new Error(`File is too large to cache. ${storageCheck.reason}. Please sign up first to upload files larger than 4MB.`);
    }

    // Generate temporary scan ID for demo
    const tempScanId = `demo-zip-${Date.now()}`;
    
    console.log('[uploadCache.cacheZipUploadForLater] Converting file to base64...');
    // Convert file to base64
    const base64Contents = await fileToBase64(file);
    
    const cachedUpload: CachedUpload = {
      source_type: 'zip',
      scan_id: tempScanId,
      name: file.name,
      type: file.type,
      size: file.size,
      contents: base64Contents,
      timestamp: Date.now(),
      demoResults,
      redirectPath: `/results/${tempScanId}`
    };

    console.log('[uploadCache.cacheZipUploadForLater] Storing in localStorage...');
    localStorage.setItem(CACHE_KEY, JSON.stringify(cachedUpload));
    console.log('[uploadCache.cacheZipUploadForLater] ✅ ZIP file cached successfully with scan_id:', tempScanId);
    return tempScanId;
  } catch (error) {
    console.error('[uploadCache.cacheZipUploadForLater] ❌ Failed to cache ZIP file:', error);
    
    // Provide user-friendly error messages
    if (error instanceof Error) {
      if (error.message.includes('quota') || error.message.includes('QuotaExceededError')) {
        throw new Error('File is too large to cache. Please sign up first to upload files larger than 4MB.');
      }
      if (error.message.includes('too large for cache')) {
        throw error; // Re-throw our custom message
      }
    }
    
    throw new Error('Failed to prepare file for upload. Please try again with a smaller file or sign up first.');
  }
}

/**
 * Store a GitHub repo URL in localStorage for later processing after login
 */
export function cacheGithubUploadForLater(repoUrl: string, demoResults?: any): string {
  console.log('[uploadCache.cacheGithubUploadForLater] Caching GitHub repo for post-login processing:', repoUrl);

  try {
    // Generate temporary scan ID for demo
    const tempScanId = `demo-github-${Date.now()}`;
    
    const cachedUpload: CachedUpload = {
      source_type: 'github',
      scan_id: tempScanId,
      repoUrl,
      timestamp: Date.now(),
      demoResults,
      redirectPath: `/results/${tempScanId}`
    };

    localStorage.setItem(CACHE_KEY, JSON.stringify(cachedUpload));
    console.log('[uploadCache.cacheGithubUploadForLater] ✅ GitHub repo cached successfully with scan_id:', tempScanId);
    return tempScanId;
  } catch (error) {
    console.error('[uploadCache.cacheGithubUploadForLater] ❌ Failed to cache GitHub repo:', error);
    throw error;
  }
}

/**
 * Legacy function for backward compatibility
 */
export async function cacheUploadForLater(file: File, demoResults?: any): Promise<void> {
  await cacheZipUploadForLater(file, demoResults);
}

/**
 * Retrieve cached upload if it exists and hasn't expired
 */
export function getCachedUpload(): CachedUpload | null {
  console.log('[uploadCache.getCachedUpload] Checking for cached upload...');

  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) {
      console.log('[uploadCache.getCachedUpload] No cached upload found');
      return null;
    }

    let cachedUpload: CachedUpload;
    try {
      cachedUpload = JSON.parse(cached);
    } catch (parseError) {
      console.error('[uploadCache.getCachedUpload] ❌ Failed to parse cached upload JSON:', parseError);
      clearCachedUpload(); // Clear corrupted cache
      return null;
    }
    
    // Validate required fields
    if (!cachedUpload.source_type || !cachedUpload.scan_id || !cachedUpload.timestamp) {
      console.error('[uploadCache.getCachedUpload] ❌ Invalid cached upload structure:', cachedUpload);
      clearCachedUpload(); // Clear corrupted cache
      return null;
    }
    
    // Check if cache has expired
    const ageHours = (Date.now() - cachedUpload.timestamp) / (1000 * 60 * 60);
    if (ageHours > CACHE_EXPIRY_HOURS) {
      console.log('[uploadCache.getCachedUpload] ⏰ Cached upload expired, clearing cache');
      clearCachedUpload();
      return null;
    }

    // Validate source_type specific fields
    if (cachedUpload.source_type === 'zip') {
      if (!cachedUpload.contents || !cachedUpload.name) {
        console.error('[uploadCache.getCachedUpload] ❌ Invalid ZIP cached upload - missing contents or name:', {
          hasContents: !!cachedUpload.contents,
          hasName: !!cachedUpload.name
        });
        clearCachedUpload();
        return null;
      }
    } else if (cachedUpload.source_type === 'github') {
      if (!cachedUpload.repoUrl) {
        console.error('[uploadCache.getCachedUpload] ❌ Invalid GitHub cached upload - missing repoUrl:', cachedUpload);
        clearCachedUpload();
        return null;
      }
    } else {
      console.error('[uploadCache.getCachedUpload] ❌ Unknown source_type:', cachedUpload.source_type);
      clearCachedUpload();
      return null;
    }

    console.log('[uploadCache.getCachedUpload] ✅ Found valid cached upload:', {
      source_type: cachedUpload.source_type,
      scan_id: cachedUpload.scan_id,
      name: cachedUpload.name || cachedUpload.repoUrl,
      ageHours: ageHours.toFixed(2)
    });

    return cachedUpload;
  } catch (error) {
    console.error('[uploadCache.getCachedUpload] ❌ Error retrieving cached upload:', error);
    clearCachedUpload(); // Clear corrupted cache
    return null;
  }
}

/**
 * Convert cached ZIP upload back to File object
 */
export function cachedUploadToFile(cachedUpload: CachedUpload): File {
  console.log('[uploadCache.cachedUploadToFile] Converting cached ZIP upload to File object');
  
  // Enhanced validation
  if (!cachedUpload) {
    throw new Error('cachedUpload is null or undefined');
  }
  
  if (cachedUpload.source_type !== 'zip') {
    throw new Error(`Cannot convert ${cachedUpload.source_type} upload to File object - only ZIP uploads supported`);
  }
  
  if (!cachedUpload.contents || typeof cachedUpload.contents !== 'string') {
    throw new Error('Cannot convert upload to File object - missing or invalid contents (expected base64 string)');
  }
  
  if (!cachedUpload.name || typeof cachedUpload.name !== 'string') {
    throw new Error('Cannot convert upload to File object - missing or invalid file name');
  }

  try {
    // Validate base64 content
    if (cachedUpload.contents.length === 0) {
      throw new Error('Cannot convert upload to File object - empty contents');
    }
    
    // Convert base64 back to Blob
    const byteCharacters = atob(cachedUpload.contents);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: cachedUpload.type || 'application/zip' });

    // Create File object with fallback values
    const file = new File([blob], cachedUpload.name, { 
      type: cachedUpload.type || 'application/zip',
      lastModified: cachedUpload.timestamp || Date.now()
    });

    console.log('[uploadCache.cachedUploadToFile] ✅ File object created:', {
      name: file.name,
      size: file.size,
      type: file.type
    });

    // Final validation
    if (file.size === 0) {
      throw new Error('Converted file has zero size - corrupted data');
    }

    return file;
  } catch (error) {
    console.error('[uploadCache.cachedUploadToFile] ❌ Error converting cached upload:', error);
    if (error instanceof Error && error.message.includes('atob')) {
      throw new Error('Cannot convert upload to File object - corrupted base64 data');
    }
    throw error;
  }
}

/**
 * Clear cached upload from localStorage
 */
export function clearCachedUpload(): void {
  console.log('[uploadCache.clearCachedUpload] Clearing cached upload');
  localStorage.removeItem(CACHE_KEY);
}

/**
 * Check if there's a pending upload
 */
export function hasCachedUpload(): boolean {
  return getCachedUpload() !== null;
}

/**
 * Helper function to convert File to base64
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        // Remove the data:application/zip;base64, prefix
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    reader.onerror = reject;
  });
} 